import { Card, CardContent } from "@/components/ui/card";

interface ConfigSummaryProps {
	configSummary: Record<string, unknown>;
}

const GROUPS: { label: string; keys: string[] }[] = [
	{
		label: "Salary",
		keys: ["basicSalary", "salaryBasis", "templateId"],
	},
	{
		label: "Shift",
		keys: ["shiftName", "weekendDays"],
	},
	{
		label: "Deductions",
		keys: [
			"enableLateDeduction",
			"lateDeductionMode",
			"enableAbsentDeduction",
			"absentDeductionBase",
		],
	},
	{
		label: "Other",
		keys: [
			"useProductiveHours",
			"productiveHoursSource",
			"overtimeEnabled",
			"deductUnpaidLeaves",
		],
	},
];

function formatLabel(key: string): string {
	return key
		.replace(/([A-Z])/g, " $1")
		.replace(/^./, (c) => c.toUpperCase())
		.trim();
}

function formatValue(value: unknown): React.ReactNode {
	if (value === null || value === undefined) {
		return <span className="text-muted-foreground font-mono text-xs">—</span>;
	}

	if (typeof value === "boolean") {
		return value ? (
			<span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
				Enabled
			</span>
		) : (
			<span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
				Disabled
			</span>
		);
	}

	if (Array.isArray(value)) {
		return (
			<span className="font-mono text-xs">{value.join(", ") || "—"}</span>
		);
	}

	if (typeof value === "object") {
		return (
			<span className="font-mono text-xs">
				{JSON.stringify(value)}
			</span>
		);
	}

	return <span className="font-mono text-xs">{String(value)}</span>;
}

function ConfigGroup({
	label,
	keys,
	configSummary,
}: {
	label: string;
	keys: string[];
	configSummary: Record<string, unknown>;
}) {
	const presentKeys = keys.filter((k) => k in configSummary);
	if (presentKeys.length === 0) return null;

	return (
		<div>
			<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
				{label}
			</p>
			<div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5">
				{presentKeys.map((key) => (
					<div key={key} className="contents">
						<span className="text-sm text-muted-foreground whitespace-nowrap">
							{formatLabel(key)}
						</span>
						<span className="text-sm">{formatValue(configSummary[key])}</span>
					</div>
				))}
			</div>
		</div>
	);
}

function UnknownKeys({
	configSummary,
}: {
	configSummary: Record<string, unknown>;
}) {
	const knownKeys = new Set(GROUPS.flatMap((g) => g.keys));
	const unknownKeys = Object.keys(configSummary).filter(
		(k) => !knownKeys.has(k),
	);

	if (unknownKeys.length === 0) return null;

	return (
		<div>
			<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
				Additional
			</p>
			<div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5">
				{unknownKeys.map((key) => (
					<div key={key} className="contents">
						<span className="text-sm text-muted-foreground whitespace-nowrap">
							{formatLabel(key)}
						</span>
						<span className="text-sm">{formatValue(configSummary[key])}</span>
					</div>
				))}
			</div>
		</div>
	);
}

export function ConfigSummary({ configSummary }: ConfigSummaryProps) {
	if (!configSummary || Object.keys(configSummary).length === 0) {
		return (
			<Card>
				<CardContent className="pt-4">
					<p className="text-sm text-muted-foreground">
						No configuration data available.
					</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent className="pt-4">
				<h4 className="font-medium mb-4">Resolved Configuration</h4>
				<div className="space-y-5">
					{GROUPS.map((group) => (
						<ConfigGroup
							key={group.label}
							label={group.label}
							keys={group.keys}
							configSummary={configSummary}
						/>
					))}
					<UnknownKeys configSummary={configSummary} />
				</div>
			</CardContent>
		</Card>
	);
}
